import { React } from 'Imports';
import { useState } from 'react';
import SingleSelect from '$Components/FilterComponents/Filters/SingleSelect';
import VPButton from '$Components/Shared/Button/VPButton';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { VideoEventResponse } from '$Generated/api';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import {
    IVideoEventFilterServiceMultiselectInjectedProps,
    VideoEventFilterServiceMultiselect,
} from '$State/VideoEventFilterFreezerServiceMultiselect';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { isEncompass } from '$Utilities/integrationPlatform';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';

const styles = require('./FinalReview.scss') as {
    container: string;
    select: string;
    errorMessage: string;
};

interface FinalReviewProps {
    videoEvent: VideoEventResponse;
    userCanEdit: boolean;
    onVideoEventUpdated: () => void;
}

const actionOptions: IDropdownItem[] = [
    { label: 'Reviewed with Driver', value: 'ReviewedWithDriver' },
    { label: 'Disciplined Driver', value: 'DisciplinedDriver' },
    { label: 'Submitted to insurance', value: 'SubmittedToInsurance' },
    { label: 'Saved for future reference', value: 'SavedForFutureReference' },
    { label: 'Terminated driver', value: 'TerminatedDriver' },
    { label: 'Mark as Dismissed', value: 'Dismissed' },
    { label: 'Dismiss as false positive', value: 'FalsePositive' },
    { label: 'Other', value: 'Other' },
];

const _FinalReview = (props: FinalReviewProps & IVideoEventServiceInjectedProps & IVideoEventFilterServiceMultiselectInjectedProps) => {
    const { videoEvent, userCanEdit, onVideoEventUpdated, videoEvents, videoEventFilterMultiselect } = props;
    const { showSnackbarMessage, handleCloseSnackbar } = useAppSnackbar();
    const liveStatus = videoEvents.getState().selectedVideoEventResult.data?.videoEventStatus ?? videoEvent.videoEventStatus;
    const isStatusLocked = liveStatus === 'Dismissed' || liveStatus === 'Completed';
    const canComplete = !isStatusLocked;
    const isDisabled = !userCanEdit;

    const [selectedAction, setSelectedAction] = useState<IDropdownItem | null>(
        videoEvent.adminResolution ? (actionOptions.find((o) => o.value === videoEvent.adminResolution) ?? null) : null,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleComplete = async () => {
        if (!selectedAction) {
            return;
        }

        if (isSubmitting) return;

        const actionStr = String(selectedAction.value);
        const isDismissAction = actionStr === 'Dismissed' || actionStr === 'FalsePositive';
        setIsSubmitting(true);

        try {
            if (isDismissAction) {
                await videoEvents.dismissVideoEvent(videoEvent.id, actionStr);
                showSnackbarMessage({
                    message: 'Event dismissed successfully. ',
                    alertType: AlertTypes.Success,
                    isEncompass: isEncompass,
                    showIcon: isEncompass,
                    onClose: handleCloseSnackbar,
                });
            } else {
                await videoEvents.completeVideoEvent(videoEvent.id, actionStr);
                videoEvents.updateLocalVideoEventStatus('Completed', videoEvent.id);
                showSnackbarMessage({
                    message: 'Event completed successfully.',
                    alertType: AlertTypes.Success,
                    isEncompass: isEncompass,
                    showIcon: isEncompass,
                    onClose: handleCloseSnackbar,
                });
            }
            videoEvents.getListOfEventIds(videoEventFilterMultiselect.getVideoEventFilter());
            onVideoEventUpdated();
        } catch {
            showSnackbarMessage({
                message: `Unable to ${isDismissAction ? 'dismiss' : 'complete'} event.`,
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReopen = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            await videoEvents.reopenEvent(videoEvent.id);
            setSelectedAction(null);
            showSnackbarMessage({
                message: 'Event reopened successfully.',
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
            videoEvents.getListOfEventIds(videoEventFilterMultiselect.getVideoEventFilter());
            onVideoEventUpdated();
        } catch {
            showSnackbarMessage({
                message: 'Unable to reopen event.',
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.select}>
                <SingleSelect
                    placeholder="Select an action ..."
                    label=""
                    hideLabel={true}
                    items={actionOptions}
                    disabled={isDisabled || isStatusLocked}
                    selectionChanged={(item) => {
                        setSelectedAction(item);
                    }}
                    selectedValue={selectedAction?.value ?? null}
                    isMobileView={false}
                    sort={false}
                    inputHeight={isEncompass ? 35 : undefined}
                    selectFontSize={12}
                    optionsFontSize={12}
                    selectIcon={isEncompass ? 'caret' : 'angle'}
                />
            </div>
            <div>
                {canComplete ? (
                    <VPButton
                        variant="filled"
                        text="Complete"
                        disabled={isDisabled || !selectedAction || isSubmitting}
                        onClick={handleComplete}
                    />
                ) : (
                    <VPButton variant="filled" text="Reopen" disabled={isDisabled || isSubmitting} onClick={handleReopen} />
                )}
            </div>
        </div>
    );
};

const FinalReview = VideoEventFilterServiceMultiselect.inject(VideoEventService.inject(_FinalReview));
export default FinalReview;
